import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Mail, Zap, Undo2, ShieldCheck, Clock, Receipt, Sparkles, ScanLine } from "lucide-react";
import RecoveryWall from "@/components/RecoveryWall";
import Footer from "@/components/Footer";
import PlanToggle, { PLANS } from "@/components/PlanToggle";

function AnimatedDollar({ target = 214 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf; let start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / 1400);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return <span>${n.toLocaleString()}</span>;
}

const RECEIPTS = [
  { m: "Netflix", a: 22.99, tag: "ACTIVE • Renews in 3d" },
  { m: "ChatGPT Plus", a: 20.00, tag: "TRIAL ENDS 14h 22m", urgent: true },
  { m: "iCloud+ 2TB", a: 9.99, tag: "APPLE • Renews 7d" },
  { m: "TikTok Shop Premium", a: 14.99, tag: "TIKTOK SHOP • 12d" },
  { m: "ReelShort Daily", a: 19.99, tag: "SHORT DRAMA • Trial", urgent: true },
];

export default function Landing() {
  const [plan, setPlan] = useState("yearly");
  const p = PLANS[plan];
  return (
    <div className="min-h-screen">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-[#282C30] rounded-full px-3 py-1 mb-6 bg-[#141618]" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] live-dot" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-[#A0A7B0]">Live • 1,284 receipts scanned last hour</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
              Recover every leaking dollar.
              <span className="block text-[#00E676]">Without your bank password.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#A0A7B0] max-w-xl leading-relaxed">
              Clawback scans your email and App Store bills, shows the exact dollars leaking, then <b className="text-[#F4F1EA]">cancels</b>,{" "}
              <b className="text-[#F4F1EA]">refunds</b>, and <b className="text-[#F4F1EA]">claims</b> that money back for you. Flat $12.99/month. No 30% cut.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="hero-cta-start"><Button className="btn-emerald px-6 h-12 text-base">Find my leaks <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
              <a href="#how" data-testid="hero-cta-how"><Button variant="outline" className="btn-outline-paper h-12 px-6">How it works</Button></a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-[#606872]">
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> read-only email</span>
              <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" /> cancel on-platform</span>
              <span className="flex items-center gap-1.5"><Undo2 className="h-3.5 w-3.5" /> real refund $</span>
            </div>
          </div>

          {/* Receipt widget */}
          <div className="lg:col-span-5 relative">
            <div className="relative bg-[#F4F1EA] text-[#121316] rounded-sm p-6 sm:p-7 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] receipt-edge-bottom" data-testid="hero-receipt">
              <div className="flex items-center justify-between border-b border-dashed border-[#121316]/30 pb-3">
                <div className="font-mono text-[10px] uppercase tracking-widest">CLAWBACK / SCAN #A72-1D</div>
                <div className="font-mono text-[10px]">{new Date().toLocaleDateString()}</div>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-mono text-xs uppercase tracking-widest">Recovered this month</span>
              </div>
              <div className="font-display font-extrabold text-5xl sm:text-6xl tracking-tighter mt-1" data-testid="hero-recovered-number">
                <AnimatedDollar target={214} />
                <span className="text-xl align-top">.00</span>
              </div>
              <div className="mt-5 space-y-2.5 font-mono text-xs">
                {RECEIPTS.map((r) => (
                  <div key={r.m} className="flex items-center justify-between border-b border-dashed border-[#121316]/20 pb-2">
                    <div>
                      <div className="uppercase text-[11px] tracking-widest">{r.m}</div>
                      <div className={`text-[10px] ${r.urgent ? "text-[#c22a00]" : "text-[#121316]/60"}`}>{r.tag}</div>
                    </div>
                    <div className="tabular-nums text-sm">-${r.a.toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest">7 leaks · 3 trials · 2 disputes</div>
                <span className="font-mono text-[10px] uppercase tracking-widest bg-[#121316] text-[#F4F1EA] px-2 py-1 rounded-sm">CLAWED BACK</span>
              </div>
            </div>
            {/* accent */}
            <div className="absolute -z-10 -bottom-6 -right-4 rotate-3 bg-[#00E676] text-[#001b09] font-mono text-[10px] px-2 py-1 uppercase tracking-widest rounded-sm">
              refund landed
            </div>
          </div>
        </div>

        {/* Ticker */}
        <div className="border-y border-[#282C30] py-3 overflow-hidden bg-[#0F1112]" data-testid="ticker">
          <div className="ticker-track font-mono text-xs uppercase tracking-widest text-[#606872] whitespace-nowrap">
            {["ticker-copy-a", "ticker-copy-b"].map((k) => (
              <div key={k} className="inline-flex gap-12">
                <span>+ $42 Zoom class action</span>
                <span>× ChatGPT trial killed → $20 saved</span>
                <span>↺ Adobe refund landed $54.99</span>
                <span>× TikTok Shop cancelled</span>
                <span>+ Apple SDA settlement $65.20</span>
                <span>↺ Grammarly refund $30</span>
                <span>× ReelShort trial killed</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECOVERY WALL */}
      <RecoveryWall />

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="how-section">
        <div className="max-w-2xl">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">THE MECHANIC</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3">
            Four things no other cancel-app does.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-10">
          {[
            { icon: ScanLine, tag: "01", title: "Find the leak, without a bank password", body: "Read-only Gmail scan parses receipts from Apple, Google Play, TikTok Shop, short-drama apps, and every web merchant. Zero access to your money." },
            { icon: Zap, tag: "02", title: "Cancel on the actual billing platform", body: "Deep links into Apple Settings → Subscriptions, Google Play Subscriptions, and the merchant's real cancel URL. Emailing Netflix doesn't kill an Apple charge — we know." },
            { icon: Undo2, tag: "03", title: "Recover money already taken", body: "One-tap starts an Apple 'I didn't intend to renew' petition, a merchant refund email, or a bank chargeback letter. Cancels stop next month. Refunds get last month back." },
            { icon: Clock, tag: "04", title: "Prove the refund landed — or escalate", body: "We watch your inbox for the credit. Nothing in 14 days? Auto-draft the next step. This is the unsolved daily frustration everyone posts about." },
          ].map((s) => (
            <Card key={s.tag} className="tactile-card p-6 rounded-md bg-[#141618]" data-testid={`how-card-${s.tag}`}>
              <div className="flex items-start gap-4">
                <div className="font-mono text-[11px] text-[#606872] tracking-widest">{s.tag}</div>
                <s.icon className="h-5 w-5 text-[#00E676] shrink-0 mt-0.5" />
              </div>
              <h3 className="font-display font-bold text-xl mt-3 tracking-tight">{s.title}</h3>
              <p className="text-sm text-[#A0A7B0] mt-2 leading-relaxed">{s.body}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-12 gap-8" data-testid="pricing-section">
        <div className="lg:col-span-5">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">PRICING</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mt-3">
            $12.99 in. $214 out. On average.
          </h2>
          <p className="mt-4 text-[#A0A7B0]">
            One price. Every leak. Every refund. Every class-action match. We take <b className="text-white">0%</b> of your payout — not $40 for a $50 check like the bait apps. Go yearly for <b className="text-white">$99</b> and save $57.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-[#A0A7B0]">
            <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /> Unlimited platform-correct cancellations</li>
            <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /> AI-drafted refund petitions & dispute letters</li>
            <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /> 14-day refund tracker with auto-escalation</li>
            <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /> Class-action matches, no commission</li>
            <li className="flex gap-2"><Sparkles className="h-4 w-4 text-[#00E676] shrink-0 mt-0.5" /> Trial tripwires with countdown alerts</li>
          </ul>
        </div>
        <div className="lg:col-span-7">
          <Card className="p-8 rounded-md tactile-card relative overflow-hidden" data-testid="pricing-card">
            <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest bg-[#00E676] text-[#001b09] px-2 py-1 rounded-sm" data-testid="pricing-badge">
              {p.badge || "recover or free"}
            </div>
            <div className="flex items-center justify-between gap-3 pr-28">
              <div className="font-mono text-[11px] uppercase tracking-widest text-[#606872]">{p.name}</div>
            </div>
            <div className="mt-3"><PlanToggle plan={plan} onChange={setPlan} testid="pricing-plan-toggle" /></div>
            <div className="mt-4 flex items-end gap-2">
              <div className="font-display font-extrabold text-6xl tracking-tighter" data-testid="pricing-amount">${p.price}</div>
              <div className="text-[#A0A7B0] pb-2">{p.label}</div>
            </div>
            <div className="font-mono text-xs text-[#606872] mt-1" data-testid="pricing-note">{p.note}</div>
            <Link to="/register" className="block mt-6">
              <Button className="btn-emerald w-full h-12 text-base" data-testid="pricing-cta-button">Find my leaks →</Button>
            </Link>
            <div className="mt-6 border-t border-dashed border-[#282C30] pt-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="font-display text-2xl font-bold text-[#00E676] tabular-nums">$214</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-1">avg / mo recovered</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold tabular-nums">16×</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-1">ROI on the sub</div>
              </div>
              <div>
                <div className="font-display text-2xl font-bold tabular-nums">0%</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872] mt-1">cut of your money</div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="faq-section">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">FAQ</div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">Questions we get.</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
          <AccordionItem value="q1" className="border-b border-[#282C30]">
            <AccordionTrigger className="font-display text-lg" data-testid="faq-q1">You don't touch my bank account?</AccordionTrigger>
            <AccordionContent className="text-[#A0A7B0]">Correct. We use read-only Gmail scopes to parse receipts. We never see or move money. Refunds land back on the original card via Apple / Google / the merchant / your bank's chargeback flow.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q2" className="border-b border-[#282C30]">
            <AccordionTrigger className="font-display text-lg" data-testid="faq-q2">Why deep-link cancel instead of "canceling for me"?</AccordionTrigger>
            <AccordionContent className="text-[#A0A7B0]">Because Apple and Google charges cannot legally be killed by emailing Netflix. Only the platform can — and it only takes one tap once you land on the right screen. That's what we do.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q3" className="border-b border-[#282C30]">
            <AccordionTrigger className="font-display text-lg" data-testid="faq-q3">What if the refund never lands?</AccordionTrigger>
            <AccordionContent className="text-[#A0A7B0]">Our 14-day tracker watches your inbox for the credit. If nothing shows up, we auto-draft the escalation — Apple appeal, executive email, or bank chargeback letter.</AccordionContent>
          </AccordionItem>
          <AccordionItem value="q4">
            <AccordionTrigger className="font-display text-lg" data-testid="faq-q4">What's the class-action feature?</AccordionTrigger>
            <AccordionContent className="text-[#A0A7B0]">We surface only high-match settlements (Zoom, Apple SDA, TikTok BIPA, etc) and file the claim in one tap. Flat sub. 0% of the payout. No $40 baiting.</AccordionContent>
          </AccordionItem>
        </Accordion>
      </section>

      <Footer />
    </div>
  );
}
